/**
 * Token Manager - Handle token storage, expiry, and validation
 * 
 * Features:
 * - Store token with timestamp
 * - Check token expiry (from token data or default 1 hour)
 * - Auto-logout on expired token
 * - Extract expiry from token response
 */

const TOKEN_KEY = 'frameworkToken';
const TOKEN_TIMESTAMP_KEY = 'tokenTimestamp';
const TOKEN_EXPIRY_KEY = 'tokenExpiry';
const DEFAULT_EXPIRY_MS = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Store token with timestamp and expiry information
 * @param {string} token - The framework token
 * @param {Object} tokenData - Decoded token data (optional, may contain expiry)
 */
export const storeToken = (token, tokenData = null) => {
  const now = Date.now();
  
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_TIMESTAMP_KEY, now.toString());
  
  // Try to extract expiry from token data
  let expiryMs = DEFAULT_EXPIRY_MS;
  
  if (tokenData) {
    // Check various possible expiry fields
    const expiryTime = tokenData.exp || 
                       tokenData.ExpiresAt || 
                       tokenData.expiresAt || 
                       tokenData.expiry ||
                       tokenData.Expiry;
    
    if (expiryTime) {
      // If expiry is a Unix timestamp (in seconds)
      if (typeof expiryTime === 'number' && expiryTime > 1000000000) {
        const expiryDate = expiryTime > 9999999999 ? expiryTime : expiryTime * 1000;
        expiryMs = expiryDate - now;
      }
      // If expiry is a date string
      else if (typeof expiryTime === 'string') {
        try {
          const expiryDate = new Date(expiryTime).getTime();
          if (!isNaN(expiryDate)) {
            expiryMs = expiryDate - now;
          }
        } catch (error) {
          console.warn('Failed to parse token expiry date:', error);
        }
      }
    }
    
    // Check for TTL or maxAge fields (in seconds)
    const ttl = tokenData.ttl || tokenData.TTL || tokenData.maxAge;
    if (ttl && typeof ttl === 'number') {
      expiryMs = ttl * 1000;
    }
  }
  
  // Ensure expiry is at least 1 minute and at most 24 hours
  expiryMs = Math.max(60 * 1000, Math.min(expiryMs, 24 * 60 * 60 * 1000));
  
  const expiryTimestamp = now + expiryMs;
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTimestamp.toString());
  
  console.log(`Token stored with expiry: ${new Date(expiryTimestamp).toLocaleString()} (${Math.round(expiryMs / 1000 / 60)} minutes)`);
};

/**
 * Get the stored token
 * @returns {string|null} - The token or null if not found
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Check if the token is expired
 * @returns {boolean} - True if token is expired, false otherwise
 */
export const isTokenExpired = () => {
  const token = getToken();
  if (!token) {
    return true;
  }
  
  const expiryTimestamp = localStorage.getItem(TOKEN_EXPIRY_KEY);
  
  if (!expiryTimestamp) {
    // If no expiry timestamp, check if token is older than default expiry
    const timestamp = localStorage.getItem(TOKEN_TIMESTAMP_KEY);
    if (!timestamp) {
      return true; // No timestamp means token is invalid
    }
    
    const tokenAge = Date.now() - parseInt(timestamp, 10);
    return tokenAge > DEFAULT_EXPIRY_MS;
  }
  
  const expiry = parseInt(expiryTimestamp, 10);
  const now = Date.now();
  const isExpired = now >= expiry;
  
  if (isExpired) {
    console.log('Token has expired');
  }
  
  return isExpired;
};

/**
 * Get time remaining until token expires (in milliseconds)
 * @returns {number} - Milliseconds until expiry, or 0 if expired
 */
export const getTokenTimeRemaining = () => {
  if (isTokenExpired()) {
    return 0;
  }
  
  const expiryTimestamp = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryTimestamp) {
    const timestamp = localStorage.getItem(TOKEN_TIMESTAMP_KEY);
    if (!timestamp) {
      return 0;
    }
    const tokenAge = Date.now() - parseInt(timestamp, 10);
    return Math.max(0, DEFAULT_EXPIRY_MS - tokenAge);
  }
  
  const expiry = parseInt(expiryTimestamp, 10);
  const now = Date.now();
  return Math.max(0, expiry - now);
};

/**
 * Get token expiry information
 * @returns {Object} - Object with expiry details
 */
export const getTokenExpiryInfo = () => {
  const token = getToken();
  if (!token) {
    return {
      hasToken: false,
      isExpired: true,
      expiresAt: null,
      remainingMs: 0,
      remainingMinutes: 0
    };
  }
  
  const isExpired = isTokenExpired();
  const remainingMs = getTokenTimeRemaining();
  const expiryTimestamp = localStorage.getItem(TOKEN_EXPIRY_KEY);
  
  return {
    hasToken: true,
    isExpired,
    expiresAt: expiryTimestamp ? new Date(parseInt(expiryTimestamp, 10)) : null,
    remainingMs,
    remainingMinutes: Math.round(remainingMs / 1000 / 60)
  };
};

/**
 * Clear all token data
 */
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_TIMESTAMP_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem('decodedTokenData');
  localStorage.removeItem('userEmail');
};

/**
 * Refresh/update token expiry time (e.g., after successful API call)
 * @param {Object} tokenData - Optional token data that may contain new expiry
 */
export const refreshTokenExpiry = (tokenData = null) => {
  const token = getToken();
  if (token) {
    storeToken(token, tokenData);
  }
};
